import { useState, useEffect } from 'react';
import { Clock, Filter, ChevronLeft, ChevronRight, ShoppingCart, Eye, CheckCircle, XCircle } from 'lucide-react';
import { getCalculationHistory } from '../../api/calculator';
import ConvertToSaleModal from './ConvertToSaleModal';

const CalculationHistory = ({ onConversionComplete }) => {
  const [calculations, setCalculations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({ page: 1, limit: 15, total: 0, totalPages: 0 });
  const [filters, setFilters] = useState({
    is_converted: '',
    start_date: '',
    end_date: '',
    addressed_to: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [selectedCalculation, setSelectedCalculation] = useState(null);
  const [showConvertModal, setShowConvertModal] = useState(false);

  useEffect(() => {
    loadHistory();
  }, [pagination.page, filters]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const queryFilters = {
        page: pagination.page,
        limit: pagination.limit
      };

      if (filters.is_converted !== '') {
        queryFilters.is_converted = filters.is_converted === 'true';
      }
      if (filters.start_date) {
        queryFilters.start_date = filters.start_date;
      }
      if (filters.end_date) {
        queryFilters.end_date = filters.end_date;
      }
      if (filters.addressed_to) {
        queryFilters.addressed_to = filters.addressed_to;
      }

      const response = await getCalculationHistory(queryFilters);
      if (response.success) {
        setCalculations(response.data);
        setPagination(prev => ({ ...prev, ...response.pagination }));
      }
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const clearFilters = () => {
    setFilters({ is_converted: '', start_date: '', end_date: '', addressed_to: '' });
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      setPagination(prev => ({ ...prev, page: newPage }));
    }
  };

  const handleConvert = (calculation) => {
    setSelectedCalculation({
      calculation_id: calculation.id,
      calculation_number: calculation.calculation_number,
      product: {
        name: calculation.product_name,
        code: calculation.product_code
      },
      cartons_needed: calculation.cartons_needed,
      price_per_carton: calculation.price_per_carton,
      estimated_cost: calculation.estimated_cost,
      customer_id: calculation.customer_id
    });
    setShowConvertModal(true);
  };

  const handleConversionComplete = () => {
    setShowConvertModal(false);
    setSelectedCalculation(null);
    loadHistory();
    onConversionComplete?.();
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <Clock className="w-5 h-5 text-accent-gold" />
          <h2 className="text-lg font-semibold text-primary-black">Calculation History</h2>
        </div>
        <button 
          className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
            showFilters 
              ? 'bg-accent-gold/10 border border-accent-gold text-accent-gold' 
              : 'bg-secondary-offWhite border border-secondary-lightGray text-primary-slate hover:bg-secondary-lightGray'
          }`}
          onClick={() => setShowFilters(!showFilters)}
        >
          <Filter className="w-4 h-4" />
          Filters
        </button>
      </div>

      {/* Filters */}
      {showFilters && (
        <div className="flex flex-wrap gap-4 p-4 bg-secondary-offWhite rounded-lg mb-4">
          <div>
            <label className="block text-xs text-primary-slate mb-1">Status</label>
            <select 
              name="is_converted" 
              value={filters.is_converted}
              onChange={handleFilterChange}
              className="px-3 py-2 bg-white border border-secondary-lightGray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-gold"
            >
              <option value="">All</option>
              <option value="true">Converted</option>
              <option value="false">Not Converted</option>
            </select>
          </div>
          <div>
            <label className="block text-xs text-primary-slate mb-1">From Date</label>
            <input
              type="date"
              name="start_date"
              value={filters.start_date}
              onChange={handleFilterChange}
              className="px-3 py-2 bg-white border border-secondary-lightGray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-gold"
            />
          </div>
          <div>
            <label className="block text-xs text-primary-slate mb-1">To Date</label>
            <input
              type="date"
              name="end_date"
              value={filters.end_date}
              onChange={handleFilterChange}
              className="px-3 py-2 bg-white border border-secondary-lightGray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-gold"
            />
          </div>
          <div>
            <label className="block text-xs text-primary-slate mb-1">Addressed To</label>
            <input
              type="text"
              name="addressed_to"
              value={filters.addressed_to}
              onChange={handleFilterChange}
              placeholder="Search by name..."
              className="px-3 py-2 bg-white border border-secondary-lightGray rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-accent-gold"
            />
          </div>
          <button 
            className="self-end px-3 py-2 text-sm text-primary-slate border border-secondary-lightGray rounded-lg hover:border-red-500 hover:text-red-500 transition-colors"
            onClick={clearFilters}
          >
            Clear
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-secondary-offWhite rounded-lg overflow-hidden">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-16 text-primary-slate">
            <div className="w-10 h-10 border-3 border-accent-gold/20 border-t-accent-gold rounded-full animate-spin mb-4"></div>
            <p>Loading calculations...</p>
          </div>
        ) : calculations.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-16 text-primary-slate">
            <Clock className="w-12 h-12 text-secondary-lightGray mb-4" />
            <h3 className="text-lg font-medium text-primary-black mb-2">No Calculations Found</h3>
            <p className="text-sm">Start calculating tiles to see history here</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full min-w-[800px]">
              <thead>
                <tr className="bg-secondary-lightGray">
                  <th className="px-4 py-3 text-left text-xs font-semibold text-primary-slate uppercase">Date</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-primary-slate uppercase">Calculation #</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-primary-slate uppercase">Product</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-primary-slate uppercase">Customer</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-primary-slate uppercase">Area</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-primary-slate uppercase">Cartons</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-primary-slate uppercase">Cost</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-primary-slate uppercase">Status</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold text-primary-slate uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-secondary-lightGray">
                {calculations.map(calc => (
                  <tr key={calc.id} className="bg-white hover:bg-secondary-offWhite transition-colors">
                    <td className="px-4 py-3 text-xs text-primary-slate whitespace-nowrap">{formatDate(calc.created_at)}</td>
                    <td className="px-4 py-3">
                      {calc.title && <div className="font-bold text-sm text-primary-black mb-0.5">{calc.title}</div>}
                      <div className="font-mono text-sm text-accent-gold">{calc.calculation_number}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Input: {calc.area_length_m}m × {calc.area_width_m}m • Wastage: {calc.wastage_percent}%
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <div>
                        <span className="text-xs font-semibold text-accent-gold">{calc.product_code}</span>
                        <p className="text-sm text-primary-black truncate max-w-[150px]">{calc.product_name}</p>
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm text-primary-black">{calc.customer_name || '—'}</td>
                    <td className="px-4 py-3 text-sm text-primary-black">{calc.total_area_sqm} m²</td>
                    <td className="px-4 py-3 text-sm font-semibold text-accent-gold">{calc.cartons_needed}</td>
                    <td className="px-4 py-3 text-sm font-semibold text-primary-black">{formatCurrency(calc.estimated_cost)}</td>
                    <td className="px-4 py-3">
                      {calc.is_converted ? (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                          <CheckCircle className="w-3 h-3" /> Converted
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-500 rounded-full text-xs font-medium">
                          <XCircle className="w-3 h-3" /> Pending
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-2">
                        {!calc.is_converted && (
                          <button 
                            className="w-8 h-8 flex items-center justify-center bg-secondary-offWhite border border-secondary-lightGray rounded-lg text-primary-slate hover:bg-accent-gold/10 hover:border-accent-gold hover:text-accent-gold transition-colors"
                            onClick={() => handleConvert(calc)}
                            title="Convert to Sale"
                          >
                            <ShoppingCart className="w-4 h-4" />
                          </button>
                        )}
                        <button 
                          className="w-8 h-8 flex items-center justify-center bg-secondary-offWhite border border-secondary-lightGray rounded-lg text-primary-slate hover:bg-secondary-lightGray transition-colors"
                          title="View Details"
                        >
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 py-4">
          <button
            className="w-9 h-9 flex items-center justify-center bg-white border border-secondary-lightGray rounded-lg text-primary-slate hover:bg-accent-gold/10 hover:border-accent-gold hover:text-accent-gold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => handlePageChange(pagination.page - 1)}
            disabled={pagination.page === 1}
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <span className="text-sm text-primary-slate">
            Page {pagination.page} of {pagination.totalPages}
          </span>
          <button
            className="w-9 h-9 flex items-center justify-center bg-white border border-secondary-lightGray rounded-lg text-primary-slate hover:bg-accent-gold/10 hover:border-accent-gold hover:text-accent-gold transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
            onClick={() => handlePageChange(pagination.page + 1)}
            disabled={pagination.page === pagination.totalPages}
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Convert Modal */}
      {showConvertModal && selectedCalculation && (
        <ConvertToSaleModal
          calculation={selectedCalculation}
          onClose={() => {
            setShowConvertModal(false);
            setSelectedCalculation(null);
          }}
          onSuccess={handleConversionComplete}
        />
      )}
    </div>
  );
};

export default CalculationHistory;
